// Keep the existing native launch surface until the first home layout settles.
// This coordinates work already in flight; it never starts or delays a request.
export const HOME_STARTUP_SETTLE_LIMIT_MS = 1200;

export function createHomeStartupPresentation() {
  let revealed = false;
  const pending = new Set<symbol>();
  let reveal: (() => void) | undefined;
  let deadline: ReturnType<typeof setTimeout> | undefined;
  let frame: number | undefined;

  const cancelFrame = () => {
    if (frame !== undefined) cancelAnimationFrame(frame);
    frame = undefined;
  };
  const finish = () => {
    if (revealed || !reveal) return;
    revealed = true;
    cancelFrame();
    clearTimeout(deadline);
    pending.clear();
    const callback = reveal;
    reveal = undefined;
    callback();
  };
  const schedule = () => {
    cancelFrame();
    if (!reveal || pending.size > 0 || revealed) return;
    // State updates and responsive layout must paint before the native cover
    // disappears. A newly registered task cancels this scheduled reveal.
    frame = requestAnimationFrame(() => {
      frame = requestAnimationFrame(finish);
    });
  };

  return {
    hold(): () => void {
      if (revealed) return () => {};
      const task = Symbol();
      pending.add(task);
      cancelFrame();
      return () => {
        if (!pending.delete(task)) return;
        schedule();
      };
    },
    requestReveal(callback: () => void) {
      if (revealed) {
        callback();
        return;
      }
      reveal = callback;
      // Duplicate header/router readiness events must not restart the deadline.
      deadline ??= setTimeout(finish, HOME_STARTUP_SETTLE_LIMIT_MS);
      schedule();
    },
  };
}

const initialHomePresentation = createHomeStartupPresentation();
const isNativeHome = () => {
  if (typeof window === "undefined") return false;
  // Only the native home owns a splash that needs this first-presentation gate.
  const hasNativeBridge = Boolean(window.ReactNativeWebView);
  const isHomeRoute = window.location.pathname === "/";
  return hasNativeBridge && isHomeRoute;
};

export function holdInitialHomePresentation(): () => void {
  return isNativeHome() ? initialHomePresentation.hold() : () => {};
}

export function revealInitialHomeWhenSettled(callback: () => void): void {
  if (isNativeHome()) initialHomePresentation.requestReveal(callback);
  else callback();
}
